import { useCallback, useContext, useEffect, useState } from 'react';
import SettingSectionTextField from '../../../components/settings/SettingSectionTextField';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import SettingSectionToggle from '../../../components/settings/SettingSectionToggle';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import { updateDynamicPage } from '../../../services/DynamicPagesService';

export const ManageDynamicPageSettingsTracking = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, domains } = useContext(DashboardContext);

  const [urlSuffix, setUrlSuffix] = useState<string>('');
  const [disableAutoAddParameters, setDisableAutoAddParameters] =
    useState<boolean>();

  useEffect(() => {
    if (!selectedDynamicPage) return;

    setUrlSuffix(selectedDynamicPage?.data()?.config?.urlSuffix || '');
    setDisableAutoAddParameters(
      selectedDynamicPage?.data()?.config?.disableAutoAddParameters,
    );
  }, [domains, selectedDynamicPage]);

  const updateDynamicPageData = useCallback(
    async (data: any) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          selectedDynamicPage.id,
          data,
        );
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, show],
  );

  const updateUrlSuffix = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().config?.urlSuffix === urlSuffix) return;

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        urlSuffix,
      },
    });
  }, [selectedDynamicPage, updateDynamicPageData, urlSuffix]);

  const updateDisableAutoAddParameters = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (
      selectedDynamicPage.data().config?.disableAutoAddParameters ===
      disableAutoAddParameters
    ) {
      return;
    }

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        disableAutoAddParameters,
      },
    });
  }, [selectedDynamicPage, updateDynamicPageData, disableAutoAddParameters]);

  return (
    <div>
      <SettingSectionTitle
        title="Tracking & URL Settings"
        description="Control how your page handles URL parameters and tracking info."
      />

      <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
        <SettingSectionTextField
          title="URL Suffix"
          value={urlSuffix}
          setValue={setUrlSuffix}
          update={updateUrlSuffix}
          editable={true}
          helpText="Add a fixed suffix to all links on your pages"
          placeholder="param1=value1&param2=value2"
          pattern="^([a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))(?:&[a-zA-Z0-9]+=(?:[a-zA-Z0-9]+|\{\{[a-zA-Z0-9]+\}\}))*$"
        />
        <SettingSectionToggle
          title="Add Parameters Automatically"
          value={!disableAutoAddParameters}
          setValue={(v: boolean) => setDisableAutoAddParameters(!v)}
          update={updateDisableAutoAddParameters}
          editable={true}
          helpText="If enabled, all url parameters will be added automatically to the links on your pages. If disabled, you will need to add them manually."
        />
      </dl>
    </div>
  );
};
